/* eslint-disable react-hooks/exhaustive-deps */
// AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import BaseAPI from "../utils/Base.API";
import {
  AUTH_POPUP_MESSAGE_TYPE,
  getGoogleRedirectEndpoint,
  isPopupCallbackRequest,
  notifyPopupAuthResultAndClose,
  redirectToGoogleLogin,
  replaceLocation,
  sanitizeParentRedirectUrl,
} from "../utils/authSession";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const isEmergencyMaintenance = ["1", "true", "yes", "y", "on"].includes(
    String(process.env.REACT_APP_EMERGENCY_MAINTENANCE || "")
      .trim()
      .toLowerCase(),
  );

  const [googleProfile, setGoogleProfile] = useState({
    email: "",
    authEmail: "",
    isAdmin: false,
    err: 0,
    err_msg: "",
  });
  const [userProfile, setUserProfile] = useState({});

  const normalizeNoTrailingSlash = (url) => (url || "").replace(/\/+$/, "");

  const getFrontendBaseUrl = () => {
    const baseFromEnv = normalizeNoTrailingSlash(process.env.REACT_APP_BASEURL || "");
    if (baseFromEnv) {
      return baseFromEnv;
    }

    const origin = window.location.origin;
    const mainDir = (process.env.REACT_APP_MAINDIR || "").replace(/^\/+|\/+$/g, "");
    return mainDir ? `${origin}/${mainDir}` : origin;
  };

  const isAuthCallbackPath = (pathname) => {
    const cleanPath = normalizeNoTrailingSlash(pathname || "");
    return cleanPath.endsWith("/auth/callback") || cleanPath === "/auth/callback";
  };

  const resolvePostLoginRedirectUrl = (rawPath) => {
    const baseUrl = getFrontendBaseUrl();
    const fallbackPath = "/employees";
    const candidatePath = String(rawPath || fallbackPath).trim() || fallbackPath;

    try {
      const parsedBase = new URL(baseUrl);
      const basePath = normalizeNoTrailingSlash(parsedBase.pathname || "");

      if (!basePath || basePath === "/") {
        return `${baseUrl}${candidatePath}`;
      }

      if (candidatePath === basePath) {
        return `${baseUrl}/`;
      }

      if (candidatePath.startsWith(`${basePath}/`)) {
        return `${baseUrl}${candidatePath.slice(basePath.length)}`;
      }

      return `${baseUrl}${candidatePath}`;
    } catch (error) {
      return `${baseUrl}${candidatePath}`;
    }
  };

  const extractEmailFromMe = (payload) => {
    if (!payload || typeof payload !== "object") {
      return "";
    }
    return payload?.email || payload?.user?.email || payload?.data?.email || "";
  };

  const extractUserFromMe = (payload) => {
    if (!payload || typeof payload !== "object") {
      return {};
    }
    return payload?.user && typeof payload.user === "object" ? payload.user : payload;
  };

  const isAdminFromUser = (user) => {
    const raw = user?.is_admin;
    if (typeof raw === "boolean") return raw;
    if (typeof raw === "number") return raw === 1;
    if (typeof raw === "string") {
      const normalized = raw.trim().toLowerCase();
      return normalized === "1" || normalized === "true" || normalized === "y";
    }
    return false;
  };

  const verifyAuthMe = async () => {
    try {
      const res = await BaseAPI.get("api/auth/me");
      const payload = res?.data?.data ?? res?.data;
      const user = extractUserFromMe(payload);
      const email = extractEmailFromMe(payload);

      if (!email) {
        throw new Error("Unable to resolve user email from /api/auth/me response");
      }

      const authEmail = user?.email || email;
      return {
        ok: true,
        email,
        authEmail,
        isAdmin: isAdminFromUser(user),
        profile: payload,
      };
    } catch (error) {
      return {
        ok: false,
        status: error?.response?.status,
        message: error?.message || "Authentication verification failed",
      };
    }
  };

  const login = () => {
    if (isEmergencyMaintenance) {
      return;
    }

    const redirectEndpoint = getGoogleRedirectEndpoint();
    if (!redirectEndpoint) {
      setGoogleProfile({
        email: "",
        authEmail: "",
        isAdmin: false,
        err: 1,
        err_msg: "Missing Base.API baseURL for OAuth redirect",
      });
      return;
    }

    const navResult = redirectToGoogleLogin();
    if (!navResult?.ok) {
      const blockedMessage =
        navResult?.mode === "popup_blocked"
          ? "Browser blocked Google login popup. Please allow popups for this site, then refresh and try again."
          : "Cannot open Google login popup. Please allow popups for this site, then refresh and try again.";
      setGoogleProfile({
        email: "",
        authEmail: "",
        isAdmin: false,
        err: 1,
        err_msg: blockedMessage,
      });
    }
  };

  const handleCallbackFlow = async () => {
    const params = new URLSearchParams(window.location.search || "");
    const error = params.get("error");
    const isPopupCallback = isPopupCallbackRequest(window.location.search || "");

    if (error) {
      if (isPopupCallback) {
        notifyPopupAuthResultAndClose({ success: false, reason: error });
        return;
      }

      setGoogleProfile({
        email: "",
        authEmail: "",
        isAdmin: false,
        err: 1,
        err_msg: `Authentication failed: ${error}`,
      });
      return;
    }

    const verifyResult = await verifyAuthMe();
    if (!verifyResult.ok) {
      if (isPopupCallback) {
        notifyPopupAuthResultAndClose({
          success: false,
          reason: verifyResult.message || `status_${verifyResult.status || "unknown"}`,
        });
        return;
      }

      if (verifyResult.status === 401 || verifyResult.status === 404) {
        setGoogleProfile({
          email: "",
          authEmail: "",
          isAdmin: false,
          err: 1,
          err_msg:
            "Authentication cookie not found after callback. Please check backend Set-Cookie/CORS/SameSite settings.",
        });
        return;
      }

      setGoogleProfile({
        email: "",
        authEmail: "",
        isAdmin: false,
        err: 1,
        err_msg: "Authentication verification failed",
      });
      return;
    }

    setGoogleProfile({
      email: verifyResult.email,
      authEmail: verifyResult.authEmail || verifyResult.email,
      isAdmin: verifyResult.isAdmin,
      err: 0,
      err_msg: "",
    });

    if (isPopupCallback) {
      notifyPopupAuthResultAndClose({ success: true });
      return;
    }

    const parentFromQuery = sanitizeParentRedirectUrl(
      params.get("parent_redirect_url")
    );
    if (parentFromQuery) {
      replaceLocation(parentFromQuery);
      return;
    }

    replaceLocation(resolvePostLoginRedirectUrl("/employees"));
  };

  const checkLoginStatus = async () => {
    if (isEmergencyMaintenance) {
      setGoogleProfile({
        email: "",
        authEmail: "",
        isAdmin: false,
        err: 0,
        err_msg: "",
      });
      return;
    }

    if (isAuthCallbackPath(window.location.pathname || "")) {
      await handleCallbackFlow();
      return;
    }

    const verifyResult = await verifyAuthMe();

    if (verifyResult.ok) {
      setGoogleProfile({
        email: verifyResult.email,
        authEmail: verifyResult.authEmail || verifyResult.email,
        isAdmin: verifyResult.isAdmin,
        err: 0,
        err_msg: "",
      });
      return;
    }

    if (verifyResult.status === 401 || verifyResult.status === 404) {
      login();
      return;
    }

    setGoogleProfile({
      email: "",
      authEmail: "",
      isAdmin: false,
      err: 1,
      err_msg: verifyResult.message,
    });
  };

  const checkIsAdminPreview = () => {
    return Boolean(googleProfile?.isAdmin);
  };

  useEffect(() => {
    checkLoginStatus();
  }, []);

  useEffect(() => {
    const onPopupAuthMessage = async (event) => {
      const data = event?.data;
      if (!data || data.type !== AUTH_POPUP_MESSAGE_TYPE) return;

      if (data.success) {
        await checkLoginStatus();
        return;
      }

      setGoogleProfile((prev) => ({
        ...prev,
        err: 1,
        err_msg: data.reason ? `Authentication failed: ${data.reason}` : "Authentication failed",
      }));
    };

    window.addEventListener("message", onPopupAuthMessage);
    return () => window.removeEventListener("message", onPopupAuthMessage);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        googleProfile,
        setGoogleProfile,
        checkLoginStatus,
        login,
        userProfile,
        setUserProfile,
        checkIsAdminPreview,
        isEmergencyMaintenance,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
